import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  getGmailStatus,
  getGmailAuthUrl,
  disconnectGmail,
  renewGmailWatch,
} from '../../../services/gmailSettingsService';

const GmailSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isRenewingWatch, setIsRenewingWatch] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getGmailStatus();
      setStatus(response.data);
    } catch (err) {
      setError('Error al obtener el estado de Gmail');
      console.error('Error fetching Gmail status:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle OAuth callback redirect
  useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      toast.success('Gmail conectado correctamente');
      searchParams.delete('gmail');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
      fetchStatus();
    } else if (gmailParam === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`Error al conectar Gmail: ${reason}`);
      searchParams.delete('gmail');
      searchParams.delete('reason');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAuthorize = useCallback(async () => {
    setIsAuthorizing(true);
    try {
      const response = await getGmailAuthUrl();
      window.location.href = response.data.authUrl;
    } catch (err) {
      toast.error('Error al generar la URL de autorización');
      console.error('Error getting auth URL:', err);
      setIsAuthorizing(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    if (!window.confirm('¿Estás seguro de que quieres desconectar Gmail? Se detendrán las notificaciones de email.')) {
      return;
    }
    setIsDisconnecting(true);
    try {
      await disconnectGmail();
      toast.success('Gmail desconectado');
      await fetchStatus();
    } catch (err) {
      toast.error('Error al desconectar Gmail');
      console.error('Error disconnecting Gmail:', err);
    } finally {
      setIsDisconnecting(false);
    }
  }, [fetchStatus]);

  const handleRenewWatch = useCallback(async () => {
    setIsRenewingWatch(true);
    try {
      await renewGmailWatch();
      toast.success('Watch renovado correctamente');
      await fetchStatus();
    } catch (err) {
      toast.error('Error al renovar el watch');
      console.error('Error renewing watch:', err);
    } finally {
      setIsRenewingWatch(false);
    }
  }, [fetchStatus]);

  const formatDate = date => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 p-6'>
        <div className='text-center py-8'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto'></div>
          <p className='text-sm text-slate-500 mt-2'>Cargando estado de Gmail...</p>
        </div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 p-6'>
        <div className='text-center py-8'>
          <p className='text-sm text-red-600'>{error}</p>
          <button
            type='button'
            onClick={fetchStatus}
            className='mt-3 text-sm text-blue-600 hover:text-blue-800'
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { connection, watch } = status || {};

  return (
    <div className='space-y-6'>
      {/* Connection Status Card */}
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <EnvelopeIcon className='w-5 h-5 text-slate-600' />
              <h3 className='text-lg font-medium text-slate-900'>Conexión Gmail</h3>
            </div>
            {connection?.connected ? (
              <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                <CheckCircleIcon className='w-3.5 h-3.5' />
                Conectado
              </span>
            ) : (
              <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                <XCircleIcon className='w-3.5 h-3.5' />
                Desconectado
              </span>
            )}
          </div>
        </div>
        <div className='p-6'>
          {connection?.connected ? (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className='text-xs text-slate-500'>Cuenta</p>
                  <p className='text-sm font-medium text-slate-900'>{connection.email}</p>
                </div>
                <div>
                  <p className='text-xs text-slate-500'>Token expira</p>
                  <p className='text-sm text-slate-700'>{formatDate(connection.tokenExpiry)}</p>
                </div>
              </div>
              <div className='flex gap-3'>
                <button
                  type='button'
                  onClick={handleAuthorize}
                  disabled={isAuthorizing}
                  className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <ArrowPathIcon className='w-4 h-4 mr-1.5' />
                  {isAuthorizing ? 'Redirigiendo...' : 'Reconectar'}
                </button>
                <button
                  type='button'
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isDisconnecting ? 'Desconectando...' : 'Desconectar'}
                </button>
              </div>
            </div>
          ) : (
            <div className='text-center py-4'>
              <EnvelopeIcon className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-sm text-slate-600 mb-4'>
                Conecta tu cuenta de Gmail para recibir y enviar emails desde ClaimUp.
              </p>
              <button
                type='button'
                onClick={handleAuthorize}
                disabled={isAuthorizing}
                className='inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isAuthorizing ? 'Redirigiendo...' : 'Conectar Gmail'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Watch Status Card */}
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-medium text-slate-900'>Notificaciones (Pub/Sub Watch)</h3>
            {watch?.active ? (
              <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                <CheckCircleIcon className='w-3.5 h-3.5' />
                Activo
              </span>
            ) : (
              <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800'>
                <ExclamationTriangleIcon className='w-3.5 h-3.5' />
                Inactivo
              </span>
            )}
          </div>
        </div>
        <div className='p-6'>
          {connection?.connected ? (
            <div className='space-y-4'>
              <div className='grid grid-cols-3 gap-4'>
                <div>
                  <p className='text-xs text-slate-500'>Expira</p>
                  <p className='text-sm text-slate-700'>{formatDate(watch?.expiration)}</p>
                </div>
                <div>
                  <p className='text-xs text-slate-500'>Último sync</p>
                  <p className='text-sm text-slate-700'>{formatDate(watch?.lastSync)}</p>
                </div>
                <div>
                  <p className='text-xs text-slate-500'>History ID</p>
                  <p className='text-sm text-slate-700 font-mono'>{watch?.historyId || '-'}</p>
                </div>
              </div>
              <button
                type='button'
                onClick={handleRenewWatch}
                disabled={isRenewingWatch || !connection?.connected}
                className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1.5 ${isRenewingWatch ? 'animate-spin' : ''}`} />
                {isRenewingWatch ? 'Renovando...' : 'Renovar Watch'}
              </button>
            </div>
          ) : (
            <p className='text-sm text-slate-500 py-2'>
              Conecta Gmail primero para activar las notificaciones.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GmailSettings;
